<template>
  <CPopover placement="bottom-end" :show-arrow="false" trigger="click">
    <template #trigger>
      <CButton v-tooltip="tooltipText" circle icon icon-placement="left">
        <template #icon>
          <CIcon icon="sliders-simple" :size="iconSize" />
        </template>
      </CButton>
    </template>
    <div class="flex flex-col w-full px-2">
      <div class="font-bold">{{ t('canvasUI.components.CTableColumnsSettings.show') }}</div>
      <div class="flex flex-col gap-2 mt-2 mb-4">
        <div
          v-for="(item, index) in tableColumnsSettings"
          :key="item.id"
          class="flex items-center gap-2"
          :class="{ selected: item.isSelected }"
          draggable="true"
          @dragover.prevent
          @dragstart="dragStart($event, item)"
          @drop="drop($event, index)"
        >
          <CIcon icon="grip-dots-vertical" size="16px" />
          <CCheckbox
            :checked="item.isSelected"
            :label="item.name"
            :value="item.id"
            @update:checked="handleItemClick(item, $event)"
          />
        </div>
      </div>
      <CButton quaternary @click="emit('restore')">
        {{ t('canvasUI.components.CTableColumnsSettings.restoreDefaults') }}
      </CButton>
    </div>
  </CPopover>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CCheckbox from '@lib/components/CCheckbox/CCheckbox.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import useThemeVars from '@lib/composables/useThemeVars'

export interface TableColumnSetting {
  id: string
  name: string
  isSelected: boolean
}

interface Props {
  tooltipText?: string
  buttonText?: string
  iconSize?: string
  tableColumnsSettings: TableColumnSetting[]
}

const props = withDefaults(defineProps<Props>(), {
  iconSize: '16px',
})

const emit = defineEmits<{
  (e: 'move', payload: { targetItemId: TableColumnSetting['id']; itemId: string }): void
  (e: 'select', payload: { id: string; checked: boolean }): void
  (e: 'restore'): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

function handleItemClick(item: TableColumnSetting, checked: boolean) {
  emit('select', { id: item.id, checked })
}

const draggedItemId = ref('')

const dragStart = (event: DragEvent, item: TableColumnSetting) => {
  draggedItemId.value = item.id // Store the ID of the dragged item

  event.dataTransfer!.effectAllowed = 'move'
  event.dataTransfer!.setData('text/plain', item.id)

  const ghostElem = document.createElement('div')
  ghostElem.style.backgroundColor = themeVars.value.primary
  ghostElem.style.color = themeVars.value.base
  ghostElem.className = 'w-24 flex justify-center items-center text-xs font-bold rounded-2'
  ghostElem.textContent = `${item.name}`

  document.body.appendChild(ghostElem)
  event.dataTransfer!.setDragImage(ghostElem, 50, 0)

  setTimeout(() => {
    document.body.removeChild(ghostElem)
  }, 0)
}
const drop = (event: DragEvent, targetIndex: number) => {
  event.preventDefault()
  const targetItemId = props.tableColumnsSettings[targetIndex].id

  emit('move', { targetItemId: targetItemId, itemId: draggedItemId.value })

  draggedItemId.value = ''
}
</script>

<style lang="scss" scoped>
:deep(.n-divider) {
  margin: 8px 0px;
}
</style>
